import { Directive, HostListener, Input } from '@angular/core';
import { LinkTypeStatsClick } from '@app-model/linkTypeStatsClick';
import { StatsTrackingService } from '@app-services/stats-tracking.service';

/**
 * Directive to declaratively track clicks from templates.
 *
 * Basic usage:
 * <button [trackClick]="linkType.PROPERTY_PHOTO_GALLERY_OPEN">Open gallery</button>
 *
 * With URL and ID:
 * <a [trackClick]="linkType.PROMOTED_BANNER_IMAGE" [trackUrl]="banner.destinationUrl" [trackId]="banner.id">
 *   <img [src]="banner.imageUrl">
 * </a>
 *
 * Note: This directive does not prevent the default click behavior; it sends tracking in parallel.
 */
@Directive({
	selector: '[trackClick]'
})
export class TrackClickDirective {
	/**
	 * Event type (required)
	 */
	@Input() trackClick!: LinkTypeStatsClick;

	/**
	 * Destination URL (optional)
	 */
	@Input() trackUrl?: string;

	/**
	 * Element ID (optional)
	 */
	@Input() trackId?: string;

	constructor(private statsTracking: StatsTrackingService) {}

	@HostListener('click', ['$event'])
	onClick(event: MouseEvent): void {
		if (!this.trackClick) {
			return;
		}

		const resolvedUrl = this.resolveTrackUrl(event);
		this.statsTracking.trackClick(this.trackClick, resolvedUrl, this.trackId).subscribe({ error: () => {} });
	}

	private resolveTrackUrl(event: MouseEvent): string | undefined {
		if (this.isValidUrl(this.trackUrl)) {
			return this.trackUrl;
		}

		// Try to derive from the clicked element (works for <a>, icons inside <a>, etc.)
		const target = (event.target as Element | null) ?? null;
		const currentTarget = (event.currentTarget as Element | null) ?? null;

		const fromTarget = this.getClosestHref(target);
		if (this.isValidUrl(fromTarget)) {
			return fromTarget;
		}

		const fromCurrentTarget = this.getClosestHref(currentTarget);
		if (this.isValidUrl(fromCurrentTarget)) {
			return fromCurrentTarget;
		}

		return undefined;
	}

	private getClosestHref(element: Element | null): string | undefined {
		if (!element) {
			return undefined;
		}

		// If the element itself is an anchor
		if (element instanceof HTMLAnchorElement) {
			return element.href || undefined;
		}

		// Or any ancestor anchor
		const closestAnchor = element.closest?.('a') as HTMLAnchorElement | null;
		return closestAnchor?.href || undefined;
	}

	private isValidUrl(url: string | undefined): boolean {
		if (!url) {
			return false;
		}

		const normalized = url.trim().toLowerCase();
		if (!normalized) {
			return false;
		}

		// Common placeholders we don't want to report as a destination.
		// Note: `element.href` resolves `href="#"` into an absolute URL ending with `/#`.
		if (
			normalized === '#' ||
			normalized === '/#' ||
			normalized === 'mailto:#' ||
			normalized === 'tel:#' ||
			normalized === 'mailto:' ||
			normalized === 'tel:'
		) {
			return false;
		}

		// Treat URLs ending in a bare "#" as placeholders, but keep real fragments ("#section")
		// and SPA-style fragments ("#/route").
		if (normalized.endsWith('#') && !normalized.endsWith('#/')) {
			return false;
		}

		return true;
	}
}
